import type { ForwardedRef, MouseEventHandler } from 'react';
import type { SetState } from '@ricoh/ria.client.core/types/component/prop.type';

import { useCallback, useImperativeHandle, useMemo } from 'react';
import { useWindowEvent } from '@ricoh/ria.client.core/hooks/app/event';
import { useUpdateEffect } from '@ricoh/ria.client.core/hooks/app/lifecycle';
import { useLoadingScreen } from '@ricoh/ria.client.core/hooks/component/loading';
import { useCoreStoreDispatch } from '@ricoh/ria.client.core/plugins/store/hooks/dispatch';
import { useCoreStoreSelector } from '@ricoh/ria.client.core/plugins/store/hooks/selector';
import { componentDialogSliceActions } from '@ricoh/ria.client.core/plugins/store/slices/component/dialog';

import type { DialogNetworkHooks, DialogNetworkProps, DialogNetworkRef } from './_.type';

/**
 * Use DialogNetwork hooks.
 *
 * @param {DialogNetworkProps} _props DialogNetwork props.
 * @param {DialogNetworkRef} ref DialogNetwork ref.
 *
 * @returns {DialogNetworkHooks} DialogNetwork hooks.
 */
export default function useDialogNetworkHooks(_props: DialogNetworkProps, ref: ForwardedRef<DialogNetworkRef>): DialogNetworkHooks {
  const dispatch = useCoreStoreDispatch();

  const loadingScreen = useLoadingScreen();

  const networkDialogState = useCoreStoreSelector((state) => state.component_dialog.network);
  const isNetworkError = useMemo<boolean>(() => Object.values(networkDialogState).some((isShow) => isShow), [networkDialogState]);

  const setShowOffline = useCallback<SetState<boolean>>(
    (state) => {
      dispatch(componentDialogSliceActions.network_setShowNetworkOffline(state(networkDialogState.offline)));
    },
    [dispatch, networkDialogState.offline],
  );

  const setShowError = useCallback<SetState<boolean>>(
    (state) => {
      dispatch(componentDialogSliceActions.network_setShowNetworkError(state(networkDialogState.error)));
    },
    [dispatch, networkDialogState.error],
  );

  const onClickRefresh = useCallback<MouseEventHandler>(() => {
    globalThis.location.reload();
  }, []);

  const onOnline = useCallback(() => {
    dispatch(componentDialogSliceActions.network_setShowNetworkOffline(false));
  }, [dispatch]);

  const onOffline = useCallback(() => {
    dispatch(componentDialogSliceActions.network_setShowNetworkOffline(true));
  }, [dispatch]);

  useWindowEvent({
    type: 'online',
    handle: onOnline,
  });

  useWindowEvent({
    type: 'offline',
    handle: onOffline,
  });

  useUpdateEffect(() => {
    if (isNetworkError) {
      loadingScreen.hide();
    }
  }, [isNetworkError]);

  useImperativeHandle(ref, () => ({}));

  return useMemo<DialogNetworkHooks>(
    () => ({
      refs: {},

      states: {
        showOfflineState: [networkDialogState.offline, setShowOffline],
        showErrorState: [networkDialogState.error, setShowError],
      },

      events: {
        onClickRefresh,
      },
    }),
    [networkDialogState.error, networkDialogState.offline, onClickRefresh, setShowError, setShowOffline],
  );
}
